import { useCallback, useEffect } from "react";

import { IS_IN_IFRAME } from "@src/env";
import { events } from "@src/shared/utils/observability/events";

import { type Ehr, sendIframeEvent } from "../machine";
import {
  iframeReadyMessage,
  isParentInitMessage,
  isWhitelistedParentDomain,
} from "../messages";

/**
 * Sets up the initial iframe ↔ parent `postMessage` flow for the embedded Stitch app.
 *
 * When `IS_IN_IFRAME` is true, posts `EL8_STITCH_IFRAME_READY` to `window.parent` once on
 * mount so the host can send initialization data.
 *
 * Registers a `window` `message` listener for `EL8_STITCH_PARENT_INIT` from allowlisted
 * parent origins and dispatches `STORE_INITIALIZED` on the iframe login machine.
 * Non-matching messages and non-allowlisted origins are ignored.
 */
export function useIframeInitialization() {
  const handleMessage = useCallback((event: MessageEvent) => {
    const hostname = new URL(event.origin).hostname;

    if (!isWhitelistedParentDomain(hostname)) {
      console.error("Invalid parent domain", hostname);
      return;
    }

    if (!isParentInitMessage(event)) return;

    const { ehr, patient, user_email } = event.data.payload;

    sendIframeEvent({
      type: "STORE_INITIALIZED",
      ehr: ehr as Ehr,
      patient: patient ?? null,
      userEmail: user_email,
    });

    if (patient) {
      events.iframePatientSync.track({
        patient_id: patient.id,
      });
    }
  }, []);

  useEffect(
    function listForParentInitMessage() {
      if (IS_IN_IFRAME) {
        window.addEventListener("message", handleMessage);

        return () => {
          window.removeEventListener("message", handleMessage);
        };
      }

      return function noop() {
        return void 0;
      };
    },
    [handleMessage],
  );

  useEffect(function postIframeReadyMessage() {
    const message = iframeReadyMessage();

    if (IS_IN_IFRAME) {
      window.parent.postMessage(message, "*");
      const referrer = document.referrer;
      let parent_origin = "unknown";
      if (referrer) {
        try {
          parent_origin = new URL(referrer).origin;
        } catch (err) {
          console.warn(
            "[useIframeInitialization] Could not parse referrer URL",
            { referrer, err },
          );
          parent_origin = "unknown";
        }
      }
      events.iframeInitialized.track({ parent_origin });
    }
  }, []);
}
